import axios from 'axios'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import { useEffect, useState, useRef, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Button, Card, Col, Row, TabPane } from 'reactstrap'
import ContentWrapper from '../../../components/Layout/ContentWrapper'
import UsersDataTable from '../components/UsersDataTable'
import UserHistoryModal from '../components/UsersHistoryModal'
import ExportTransfersModal from '../components/ExportTransfersModal'
import ExportSwitchesModal from '../components/ExportSwitchesModal'
import DocumentInputModal from '../../../components/Users/DocumentInputModal'
import { getColumns } from '../utils/columns'
import { _exportData, data } from '../utils/data'
import { getOptions } from '../utils/options'
import { RESTRICTED_MANAGERS } from '../../../utils/consts'
import { CloudDownload } from '@material-ui/icons'
import { EPermissionsKeys } from '../../../components/Layout/permissionEnums'
import Swal from 'sweetalert2'
import pubsub from 'pubsub-js'
const Users = () => {
  const history = useHistory()
  const [users, setUsers] = useState([])
  const [count, setCount] = useState(20)
  const [total, setTotal] = useState(0)
  const [projects, setProjects] = useState([])
  const [roles, setRoles] = useState([])
  const [departments, setDepartments] = useState([])
  const [activities, setActivities] = useState([])
  const [workingStatus, setWorkingStatus] = useState([])
  const [languages, setLanguages] = useState([])
  const [selectedFilters, setSelectedFilters] = useState({
    page: 0,
    searchText: '',
    fromDateValue: null,
    toDateValue: null,
    language: undefined,
    project: undefined,
    department: undefined,
    function: undefined,
    activity: undefined,
    workingStatus: undefined,
    office: undefined,
    sortParam: undefined,
    sortType: undefined
  })
  const [renderFilters, setRenderFilters] = useState({
    activity: '',
    workingStatus: '',
    project: '',
    department: '',
    function: '',
    language: '',
    offices: ''
  })
  const [showFilters, setShowFilters] = useState({
    department: true,
    activity: false,
    workingStatus: false,
    identityID: false,
    createdDate: false,
    city: false,
    manager: false,
    teamLeader: false,
    language: true,
    offices: false
  })

  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalData, setModalData] = useState({})
  const [lastImportDate, setLastImportDate] = useState(null)
  const token = localStorage.getItem('token')
  const decodedToken = token ? jwtDecode(token) : null
  const userLevel = decodedToken ? decodedToken.user_level : null
  const permissionsKeys = decodedToken ? decodedToken.permissions_keys : []
  const hasMonthlyWorkingHoursAccess = permissionsKeys?.includes(EPermissionsKeys.ALLOWED_TO_SEE_MONTHLY_WORKING_HOURS)
  const today = moment().format('DD-MM-YYYY, HH:mm')
  const axiosCancelSource = axios.CancelToken.source()
  const offices = ['Prishtinë HQ', 'Ferizaj', 'Vushtrri', 'Prizren', 'HO-Prishtinë', 'HO-Ferizaj', 'HO-Vushtrri', 'HO-Prizren']
  const restrictedManagers = RESTRICTED_MANAGERS?.split(',')

  // Ref to track initial mount
  let isInitialMount = useRef(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const [projectsRes, rolesRes, departmentsRes, activitiesRes, workingStatusRes, languagesRes, usersRes] = await Promise.all([
          axios.get('projects', { cancelToken: axiosCancelSource.token }),
          axios.get('roles', { cancelToken: axiosCancelSource.token }),
          axios.get('departments', { cancelToken: axiosCancelSource.token }),
          axios.get('activities', { cancelToken: axiosCancelSource.token }),
          axios.get('works', { cancelToken: axiosCancelSource.token }),
          axios.get('languages', { cancelToken: axiosCancelSource.token }),
          axios.get(`users/${count}/${selectedFilters.page + 1}`, { params })
        ])
        setProjects(projectsRes.data.success ? projectsRes.data.data : [])
        setRoles(rolesRes.data.success ? rolesRes.data.data : [])
        setDepartments(departmentsRes.data.success ? departmentsRes.data.data : [])
        setActivities(activitiesRes.data.success ? activitiesRes.data.data : [])
        setWorkingStatus(workingStatusRes.data.success ? workingStatusRes.data.data : [])
        setLanguages(languagesRes.data.success ? languagesRes.data.data : [])
        setTotal(usersRes.data.data.totalDocs)
        setUsers(usersRes.data.data.docs)
      } catch (e) {
        console.log('error', e)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
    return () => {
      axiosCancelSource.cancel('Component unmounted')
    }
  }, [])

  const fetchLastImportInfo = async () => {
    if (!hasMonthlyWorkingHoursAccess) return
    try {
      const response = await axios.get('monthlyTargetHours/lastImportInfo')
      if (response.data.success && response.data.data?.created_date) {
        setLastImportDate(response.data.data.created_date)
      }
    } catch (error) {
      // Silently fail if no file exists yet
      if (error.response?.status !== 404) {
        console.error('Error fetching last import info:', error)
      }
    }
  }

  useEffect(() => {
    fetchLastImportInfo()

    // Listen for successful imports to refresh the date
    const token = pubsub.subscribe('monthlyTargetHoursImportSuccess', () => {
      fetchLastImportInfo()
    })

    return () => {
      pubsub.unsubscribe(token)
    }
  }, [hasMonthlyWorkingHoursAccess])

  const params = {
    function: selectedFilters.function || undefined,
    project: selectedFilters.project || undefined,
    activity: selectedFilters.activity || undefined,
    work: selectedFilters.workingStatus || undefined,
    'department[]': selectedFilters.department || undefined,
    'languages[]': selectedFilters.language || undefined,
    office_location: selectedFilters.office || undefined,
    search: selectedFilters.searchText || undefined,
    fromDate: selectedFilters.fromDateValue || undefined,
    toDate: selectedFilters.toDateValue || undefined,
    sortParam: selectedFilters.sortParam || undefined,
    sortType: selectedFilters.sortType || undefined
  }

  const exportUsers = async () => {
    _exportData(`users/export`, `Users (${today}).csv`, 'Duke shkarkuar të dhënat për përdoruesit.', params)
  }
  const exportManagerRestricted = async () => {
    const exportParams = {
      ...params,
      user_id: decodedToken?.user_id
    }
    _exportData(`users/export/managerRestricted`, `Users (${today}).csv`, 'Duke shkarkuar të dhënat për përdoruesit.', exportParams)
  }
  const exportWarnings = () => {
    _exportData('/userwarnings/csv', `Warnings (${today}).csv`, 'Duke shkarkuar të dhënat për vërejtjet.')
  }
  const exportRejoiners = () => {
    _exportData('/users/rejoiners', `Të rikthyerit (${today}).csv`, 'Duke shkarkuar të dhënat për të rikthyerit.')
  }
  const exportActiveAndInactiveUsers = () => {
    _exportData('/users/activeandinactivecsv', `Përdoruesit AKTIV-JOAKTIV (${today}).csv`, 'Duke shkarkuar të dhënat për përdoruesit.')
  }
  const exportAuditReportActiveAndInactiveUsers = () => {
    _exportData('/users/auditreport-activeandinactive', `Raporti për auditor (${today}).csv`, 'Duke shkarkuar të dhënat për përdoruesit.')
  }

  const downloadMonthlyTargetHoursTemplate = () => {
    setModalData({
      modalType: 'downloadMonthlyTargetHoursTemplate',
      title: 'Shkarko Monthly Target Hours Template',
      description: 'Zgjedhni muajin dhe vitin për të shkarkuar template-in e Monthly Target Hours.',
      fields: [
        {
          type: 'monthpicker',
          name: 'month',
          label: 'Muaji dhe viti:'
        }
      ]
    })
    setIsModalOpen(true)
  }

  const downloadLastMonthlyTargetImport = async () => {
    try {
      Swal.fire({
        title: 'Ju lutem prisni...',
        html: 'Duke shkarkuar fajllin!',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const response = await axios.get('monthlyTargetHours/lastImport', {
        responseType: 'blob',
        withCredentials: false
      })

      Swal.close()

      const contentDisposition = response.headers['content-disposition']
      let filename = 'monthly_target_import.csv'
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      pubsub.publish('showNotification', {
        message: 'Fajlli u shkarkua me sukses!',
        type: 'success'
      })
    } catch (error) {
      Swal.close()
      const message = error.response?.status === 404 ? 'Nuk u gjet asnjë fajll importi' : 'Gabim gjatë shkarkimit të fajllit'
      pubsub.publish('showNotification', {
        message,
        type: 'error'
      })
    }
  }

  const importMonthlyTargetHours = () => {
    setModalData({
      modalType: 'monthlyTargetHours',
      title: 'Importo Monthly Target Hours',
      description:
        'Ngarkoni fajllin per importim te te dhenave. Fajlli duhet te jete ne format CSV. Ju lutem zgjedhni edhe daten per muajin qe vlejne Monthly Target Hours.',
      fields: [
        {
          type: 'monthpicker',
          name: 'month',
          label: 'Muaji dhe viti:'
        },
        {
          type: 'file',
          name: 'file_import',
          label: 'Fajlli per import:'
        }
      ]
    })
    setIsModalOpen(true)
  }

  const handleModalComplete = async (data) => {
    if (modalData.modalType === 'downloadMonthlyTargetHoursTemplate') {
      const selectedMonth = data.state.month

      if (!selectedMonth) {
        return pubsub.publish('showNotification', {
          message: 'Ju lutem zgjedhni muajin dhe vitin',
          type: 'error'
        })
      }

      setIsModalOpen(false)

      const targetMonth = selectedMonth.month() + 1
      const targetYear = selectedMonth.year()

      const startDate = moment([targetYear, targetMonth - 2, 26])
      const endDate = moment([targetYear, targetMonth - 1, 25])

      const dateHeaders = []
      const currentDate = startDate.clone()

      while (currentDate.isSameOrBefore(endDate)) {
        dateHeaders.push(currentDate.format('DD/MM/YYYY'))
        currentDate.add(1, 'day')
      }

      const headers = ['name', 'user_id', 'partner_id', ...dateHeaders]
      const csvContent = headers.join(',') + '\n'

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `monthlyTargetHours_template_${selectedMonth.format('MM_YYYY')}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return
    }

    const file = data.state.file_import[0]
    const filename = file.name.split('.').pop()

    if (filename !== 'csv') {
      return pubsub.publish('showNotification', {
        message: 'Fajlli i zgjedhur duhet te jete ne formatin CSV',
        type: 'error'
      })
    }

    setIsModalOpen(false)
    setModalLoading(true)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('target_month', data.state.month.format('M'))
    formData.append('target_year', data.state.month.format('YYYY'))

    Swal.fire({
      title: 'Ju lutem prisni...',
      html: 'Duke kontrolluar të dhënat!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    try {
      // First check the import
      const { data: checkResponse } = await axios.post('monthlyTargetHours/check', formData)

      Swal.close()
      if (!checkResponse.success) {
        return pubsub.publish('showNotification', {
          message: checkResponse.message,
          type: 'error'
        })
      }

      // Navigate to process page with validation data
      history.push({
        pathname: '/processMonthlyTargetHours',
        state: {
          importData: {
            ...checkResponse,
            fileData: file
          }
        }
      })
    } catch (error) {
      Swal.close()
      const message = error.response?.data?.message || 'Gabim gjatë kontrollimit të fajllit'
      pubsub.publish('showNotification', {
        message,
        type: 'error'
      })
    } finally {
      setModalLoading(false)
    }
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  const _getUsers = async () => {
    try {
      setIsLoading(true)
      const usersRes = await axios.get(`users/${count}/${selectedFilters.page + 1}`, { params })
      if (usersRes.data.success) {
        setTotal(usersRes.data.data.totalDocs)
        setUsers(usersRes.data.data.docs)
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    } else {
      _getUsers()
    }
  }, [
    count,
    selectedFilters.page,
    selectedFilters.function,
    selectedFilters.project,
    selectedFilters.activity,
    selectedFilters.workingStatus,
    selectedFilters.department,
    selectedFilters.language,
    selectedFilters.office,
    selectedFilters.searchText,
    selectedFilters.fromDateValue,
    selectedFilters.toDateValue,
    selectedFilters.sortParam,
    selectedFilters.sortType
  ])

  const columns = getColumns(
    languages,
    roles,
    projects,
    departments,
    activities,
    workingStatus,
    offices,
    showFilters,
    renderFilters,
    selectedFilters
  )
  const options = getOptions(
    total,
    count,
    setCount,
    languages,
    roles,
    projects,
    offices,
    departments,
    activities,
    workingStatus,
    exportUsers,
    renderFilters,
    setRenderFilters,
    selectedFilters,
    setSelectedFilters,
    showFilters,
    setShowFilters
  )

  return (
    <ContentWrapper>
      <div className='content-heading'>
        <div>Perdoruesit</div>
      </div>
      {[1, 5, 6].includes(userLevel) && (
        <div className='d-flex justify-content-between align-items-center mb-3 ml-auto'>
          <div>
            <Button color='info' className='btn-oval mb-2' onClick={exportWarnings}>
              Shkarko të gjitha vërejtet
            </Button>
            <ExportTransfersModal departments={departments} />
            <ExportSwitchesModal departments={departments} />
            <Button color='info' className='btn-oval mx-2 mb-2' onClick={exportActiveAndInactiveUsers}>
              Shkarko të gjithë përdoruesit AKTIV & JO-AKTIV
            </Button>
            <Button color='info' className='btn-oval mx-2 mb-2' onClick={exportRejoiners}>
              Shkarko të rikthyerit
            </Button>
            {userLevel === 1 && (
              <Button color='info' className='btn-oval mx-2 mb-2' onClick={exportAuditReportActiveAndInactiveUsers}>
                Shkarko raportin për auditor
              </Button>
            )}
            <UserHistoryModal projects={projects} functions={roles} departments={departments} languages={languages} />
          </div>
          <Link to='/editUsers/'>
            <Button color='success' className='btn-oval mb-2'>
              Shto një Përdorues
            </Button>
          </Link>
        </div>
      )}
      {restrictedManagers?.includes(decodedToken.user_id) && (
        <div>
          <Button
            style={{
              margin: 10,
              background: '#0000FF',
              color: 'white',
              borderRadius: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onClick={exportManagerRestricted}
          >
            Shkarko Userat
            <span>
              <CloudDownload />
            </span>
          </Button>
        </div>
      )}
      {/* // custom-monthly-target-bonus-task-commented-out */}
      {/* {hasMonthlyWorkingHoursAccess && (
        <div className='mb-3'>
          <Button color='info' className='btn-oval mb-2' onClick={exportManagerRestricted}>
            Download Users
          </Button>
          <Button color='primary' className='btn-oval mb-2 mx-2' onClick={downloadMonthlyTargetHoursTemplate}>
            Download Monthly Target Hours Template
          </Button>
          <Button color='success' className='btn-oval mb-2 mx-2' onClick={importMonthlyTargetHours}>
            Import Monthly Target Hours
          </Button>
          <div className='d-inline-block' style={{ verticalAlign: 'top' }}>
            <Button color='warning' className='btn-oval mb-2 mx-2' onClick={downloadLastMonthlyTargetImport}>
              Download Last Monthly Target Import
            </Button>
            {lastImportDate && (
              <div className='text-muted small' style={{ marginTop: '2px', textAlign: 'center', width: '100%' }}>
                {moment(lastImportDate).format('DD-MM-YYYY, HH:mm')}
              </div>
            )}
          </div>
        </div>
      )} */}
      <DocumentInputModal
        className={modalLoading ? 'whirl standard' : ''}
        onComplete={handleModalComplete}
        isOpen={isModalOpen}
        modalData={modalData}
        toggleModal={toggleModal}
      />
      <Row>
        <Col>
          <Card className='shadow bg-white'>
            <TabPane tabId='1'>
              <UsersDataTable
                columns={columns}
                options={options}
                data={data(users)}
                setSelectedFilters={setSelectedFilters}
                selectedFilters={selectedFilters}
                isLoading={isLoading}
              />
            </TabPane>
          </Card>
        </Col>
      </Row>
    </ContentWrapper>
  )
}

export default Users
